import * as React from 'react';
import styled, { css } from 'styled-components';
import { rem, rgba } from 'polished';
import { SvgIcon, IconType } from '../General/SvgIcon';
import { __, __r } from 'react-i18n';
import { SanitasNavigation } from '../../..';
import { resolveCategoryUrl } from '../../../utilities/selectors';
import { Link } from 'react-router';

interface Props {
  id: string;
  category_name: string;
  url: string;
  children?: Array<{ id: string; category_name: string; url: string }>;
  isCurrent: boolean;
  location: any;
  onClick?: () => void;
}

export const NavigationItem = ({
  id,
  category_name,
  url,
  children = [],
  isCurrent = false,
  location,
  onClick,
}: Props) => {
  const [isActive, setIsActive] = React.useState(false);

  const toggleAccordion = () => {
    if (children && children.length > 0) {
      setIsActive(!isActive);
    }
  };

  const renderDropdown = () => {
    return (
      <SanitasNavigationCover>
        <SanitasNavigation
          onClick={onClick}
          children={children}
          location={location}
        />
      </SanitasNavigationCover>
    );
  };

  const renderIcon = () => {
    return (
      <SvgIcon
        icon={!isActive ? IconType.plus : IconType.minus}
        alt={__('Toggle dropdown')}
        viewBox="0, 0, 9, 9"
        width={6}
        height={6}
        marginRight={8}
        fill={'#3962A8'}
        cursor="pointer"
      />
    );
  };

  const renderItem = () => {
    return (
      <NavItem
        isDropdownItem={children && children.length > 0}
        isActive={isActive}
        onClick={toggleAccordion}
        isCurrent={isCurrent}
      >
        <DropdownToggleCover>
          {children && children.length > 0 ? renderIcon() : null}
        </DropdownToggleCover>
        <NavLink
          to={resolveCategoryUrl(id, url)}
          title={category_name}
          onClick={onClick}
        >{`${category_name}`}</NavLink>
      </NavItem>
    );
  };

  const renderLink = () => {
    return (
      <NavItem
        isDropdownItem={children && children.length > 0}
        isActive={isActive}
        onClick={toggleAccordion}
        isLinkCover={true}
        isCurrent={isCurrent}
      >
        <BaseLink
          to={resolveCategoryUrl(id, url)}
          title={category_name}
          onClick={onClick}
        >{`${category_name}`}</BaseLink>
      </NavItem>
    );
  };

  return (
    <NavItemCover>
      {children && children.length === 0 && renderLink()}
      {children && children.length > 0 && renderItem()}
      {children && children.length > 0 && isActive && renderDropdown()}
    </NavItemCover>
  );
};

const SanitasNavigationCover = styled.div`
  overflow: hidden;
  transition: max-height 0.25s ease-in;

  & > nav > ul > li {
    & > div:first-of-type {
      border-radius: 0;
    }

    &:first-child {
      & > div:first-of-type {
        border-radius: ${rem(4)} ${rem(4)} 0 0;
      }
    }

    &:last-child {
      & > div:first-of-type {
        border-radius: 0 0 ${rem(4)} ${rem(4)};
      }
    }
  }
`;

const NavItemCover = styled.li`
  list-style: none;

  li > div {
    &::before {
      opacity: 0.12;
    }
  }
`;

const NavItem = styled.div<{
  isDropdownItem: boolean;
  isActive: boolean;
  isLinkCover?: boolean;
  isCurrent: boolean;
}>`
  position: relative;
  border-radius: ${props => (props.isDropdownItem ? rem(4) : rem(4))};
  background-color: transparent;
  overflow: hidden;
  margin-bottom: ${rem(2)};
  cursor: ${props => (props.isDropdownItem ? 'pointer' : 'initial')};
  padding: ${props =>
    props.isLinkCover ? '0' : `${rem(14)} ${rem(24)} ${rem(15)} ${rem(30)}`};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.tertiary};
    opacity: ${props => (props.isActive ? '0.12' : '0.04')};
    z-index: -1;
    transition: all 0.15s ease-in;
  }

  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colors.tertiary};
    color: ${({ theme }) => theme.colors.white};
    transition: all 0.15s ease-in;

    svg {
      fill: ${({ theme }) => theme.colors.white} !important;
    }

    a {
      color: ${({ theme }) => theme.colors.white};
      transition: all 0.15s ease-in;
    }
  }

  ${({ theme, isCurrent }) =>
    // `${isCurrent ? rem(2) : rem(1)} solid ${
    //   isCurrent ? theme.colors.primary : theme.colors.borderColor
    // }`};
    `${
      isCurrent
        ? `
      background-color: ${theme.colors.tertiary};
      svg {
        fill: ${theme.colors.white} !important;
      }
  
      a {
        color: ${theme.colors.white};
      }
    `
        : ``
    }`};
`;

const linkLikeCss = css`
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  color: ${({ theme }) => theme.colors.tertiary};
  font-weight: 400;
  font-family: 'Roboto', sans-serif;
  display: inline-block;
  text-decoration: none;
  transition: all 0.15s ease-in;

  &:hover {
    text-decoration: underline;
  }
`;

const NavLink = styled(Link)`
  ${linkLikeCss}
`;

const BaseLink = styled(Link)`
  ${linkLikeCss};
  display: block;
  width: 100%;
  padding: ${rem(14)} ${rem(24)} ${rem(15)} ${rem(30)};
`;

const DropdownToggleCover = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: ${rem(21)};
  left: ${rem(16)};
`;
